import { formatForContext } from "@/features/hoverIdle/resources/numberFormat";
import { compareHuge, type HugeNumber } from "@/shared/numbers";
import type { HovSkillDefinition, HovSkillId } from "./hovSkillDefinitions";

export interface HovSkillDisplayCoordinate {
  readonly row: number;
  readonly column: number;
}

export interface HovSkillReferenceParts {
  readonly skillId: HovSkillId;
  readonly coordinateLabel: string;
  readonly displayRow: number;
  readonly displayColumn: number;
}

export interface HovSkillExternalMultiplierRewardParts extends HovSkillReferenceParts {
  readonly multiplierLabel: string;
}

// Internal 0-based (rowIndex, columnIndex) -> player-facing 1-based pair.
export function toHovSkillDisplayCoordinate(
  rowIndex: number,
  columnIndex: number,
): HovSkillDisplayCoordinate {
  return { row: rowIndex + 1, column: columnIndex + 1 };
}

// Internal 0-based index -> player-facing label, e.g. (0, 0) -> "[1, 1]".
export function formatHovSkillCoordinate(
  rowIndex: number,
  columnIndex: number,
): string {
  const { row, column } = toHovSkillDisplayCoordinate(rowIndex, columnIndex);
  return `[${row}, ${column}]`;
}

export function getHovSkillReferenceParts(
  definition: HovSkillDefinition,
): HovSkillReferenceParts {
  const { row, column } = toHovSkillDisplayCoordinate(
    definition.row,
    definition.column,
  );

  return {
    skillId: definition.id,
    coordinateLabel: formatHovSkillCoordinate(
      definition.row,
      definition.column,
    ),
    displayRow: row,
    displayColumn: column,
  };
}

export function isHovSkillExternalMultiplierActive(
  multiplier: HugeNumber,
): boolean {
  return compareHuge(multiplier, 1) !== 0;
}

export function formatHovSkillExternalMultiplier(
  multiplier: HugeNumber,
): string {
  return `×${formatForContext(multiplier, "tooltip")}`;
}

export function getHovSkillExternalMultiplierRewardParts(
  definition: HovSkillDefinition,
  multiplier: HugeNumber,
): HovSkillExternalMultiplierRewardParts {
  return {
    ...getHovSkillReferenceParts(definition),
    multiplierLabel: formatHovSkillExternalMultiplier(multiplier),
  };
}
